import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, map } from 'rxjs';

import { CacheStatus, StatusFilter } from '../models/cache-models';

/**
 * Service for tracking the status of cache operations.
 * Provides a unified way to report and track loading states.
 */
@Injectable({
  providedIn: 'root'
})
export class CacheStatusService {
  /**
   * Default status (nothing loading).
   */
  private readonly DEFAULT_STATUS: CacheStatus = {
    isLoading: false,
    progress: 100
  };

  /**
   * Subject for tracking all cache statuses.
   */
  private status = new BehaviorSubject<CacheStatus[]>([]);

  /**
   * Watch status updates, optionally filtered.
   *
   * @param filter Optional filter to limit which statuses are observed.
   * @returns Observable of matching cache statuses.
   */
  public watchStatus(filter?: StatusFilter): Observable<CacheStatus[]> {
    if (!filter) {
      return this.status.asObservable();
    }

    return this.status.pipe(
      map(statuses => statuses.filter(status => this.matchesFilter(status, filter)))
    );
  }

  /**
   * Watch a specific store's loading status.
   *
   * @param storeId The ID of the store to watch.
   * @param projectId Optional project ID to further refine the filter.
   * @returns Observable of boolean indicating if the store is loading.
   */
  public isLoading(storeId: string, projectId?: string): Observable<boolean> {
    return this.watchStatus({ storeId, projectId }).pipe(
      map(statuses => statuses.some(status => status.isLoading))
    );
  }

  /**
   * Watch progress for a specific store or item.
   *
   * @param storeId The ID of the store to watch.
   * @param itemId Optional item ID to further refine the filter.
   * @param projectId Optional project ID to further refine the filter.
   * @returns Observable of number representing progress (0-100).
   */
  public watchProgress(storeId: string, itemId?: string | number, projectId?: string): Observable<number> {
    return this.watchStatus({ storeId, itemId, projectId }).pipe(
      map(statuses => {
        if (statuses.length === 0) {
          return 100; // Default to complete if no status found
        }

        // If multiple operations, return the average progress
        const sum = statuses.reduce((acc, status) => acc + status.progress, 0);

        return Math.round(sum / statuses.length);
      })
    );
  }

  /**
   * Update or add a status entry.
   *
   * @param update The status update to apply.
   */
  public updateStatus(update: CacheStatus): void {
    const current = this.status.getValue();

    // Find existing status with matching identifiers
    const index = current.findIndex(s =>
      s.storeId === update.storeId &&
      s.itemId === update.itemId &&
      s.projectId === update.projectId &&
      s.operation === update.operation
    );

    if (index >= 0) {
      // Check if the update actually changes anything
      const existingStatus = current[index];
      const hasChanged = existingStatus.isLoading !== update.isLoading ||
                         existingStatus.progress !== update.progress ||
                         existingStatus.error !== update.error;

      // Only update if something changed
      if (hasChanged) {
        // Update existing status
        const updated = [...current];

        updated[index] = { ...current[index], ...update };
        this.status.next(updated);
      }
    } else {
      // Add new status
      this.status.next([...current, update]);
    }

    // Clean up completed statuses
    this.cleanupCompletedStatuses();
  }

  /**
   * Clear status for a specific store or item.
   *
   * @param storeId The ID of the store to clear.
   * @param itemId Optional item ID to further refine what's cleared.
   * @param projectId Optional project ID to further refine what's cleared.
   */
  public clearStatus(storeId: string, itemId?: string | number, projectId?: string): void {
    const current = this.status.getValue();

    // Filter out matching statuses
    const updated = current.filter(s =>
      s.storeId !== storeId ||
      (itemId && s.itemId !== itemId) ||
      (projectId && s.projectId !== projectId)
    );

    this.status.next(updated);
  }

  /**
   * Clear all statuses.
   */
  public clearAllStatus(): void {
    this.status.next([]);
  }

  /**
   * Check if a status matches the provided filter.
   *
   * @param status The status to check.
   * @param filter The filter to apply.
   * @returns True if the status matches the filter.
   */
  private matchesFilter(status: CacheStatus, filter: StatusFilter): boolean {
    // Check each filter property if it exists
    if (filter.storeId && status.storeId !== filter.storeId) {
      return false;
    }

    if (filter.itemId && status.itemId !== filter.itemId) {
      return false;
    }

    if (filter.projectId && status.projectId !== filter.projectId) {
      return false;
    }

    if (filter.areaId && status.areaId !== filter.areaId) {
      return false;
    }

    return true;
  }

  /**
   * Remove completed statuses after a short delay.
   */
  private cleanupCompletedStatuses(): void {
    const current = this.status.getValue();

    // Remove statuses that are complete (progress = 100 and not loading)
    const updated = current.filter(s =>
      s.isLoading || s.progress < 100
    );

    if (updated.length !== current.length) {
      this.status.next(updated);
    }
  }
}
